import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

const LOCALES = [
  { code: 'de', name: t('德国'), currency: 'EUR' }, { code: 'fr', name: t('法国'), currency: 'EUR' },
  { code: 'ja', name: t('日本'), currency: 'JPY' }, { code: 'ko', name: t('韩国'), currency: 'KRW' },
  { code: 'es', name: t('西班牙'), currency: 'EUR' }, { code: 'it', name: t('意大利'), currency: 'EUR' },
];

// ═══ 市场与术语库 ═══
function MarketsView() {
  const [markets, setMarkets] = useState<any[]>([]);
  const [glossary, setGlossary] = useState<any[]>([]);
  const [mLocale, setMLocale] = useState('de');
  const [gForm, setGForm] = useState({ term: '', locale: '*', translation: '' });

  const load = useCallback(() => {
    get('/api/v1/markets').then((r: any) => setMarkets(r.data || [])).catch(() => {});
    get('/api/v1/glossary').then((r: any) => setGlossary(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const addMarket = async () => {
    const loc = LOCALES.find(l => l.code === mLocale)!;
    try { await post('/api/v1/markets', { locale: loc.code, market_name: loc.name, currency: loc.currency }); load(); }
    catch (e: any) { alert(e.message); }
  };

  const addTerm = async () => {
    if (!gForm.term) return alert('请填写术语');
    try { await post('/api/v1/glossary', { ...gForm, translation: gForm.translation || null }); setGForm({ term: '', locale: '*', translation: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>目标市场（{markets.length}）</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <select style={S.input} value={mLocale} onChange={e => setMLocale(e.target.value)}>
            {LOCALES.map(l => <option key={l.code} value={l.code}>{l.name}（{l.code}）</option>)}
          </select>
          <button style={S.btnPrimary} onClick={addMarket}>{t('＋ 添加市场')}</button>
        </div>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('语言')}</th><th style={S.th}>{t('市场')}</th><th style={S.th}>{t('货币')}</th><th style={S.th}>{t('单位制')}</th><th style={S.th}></th></tr></thead>
          <tbody>
            {markets.map((m: any) => (
              <tr key={m.id}>
                <td style={S.td}>{m.locale}</td><td style={S.td}>{m.market_name}</td>
                <td style={S.td}>{m.currency}</td><td style={S.td}>{m.unit_system}</td>
                <td style={S.td}><button style={S.btn} onClick={async () => { await del(`/api/v1/markets/${m.id}`); load(); }}>{t('移除')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('术语库（品牌专词留空翻译 = 保持原文）')}</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...S.input, width: 180 }} placeholder={t('术语/品牌词')} value={gForm.term} onChange={e => setGForm({ ...gForm, term: e.target.value })} />
          <select style={S.input} value={gForm.locale} onChange={e => setGForm({ ...gForm, locale: e.target.value })}>
            <option value="*">{t('全部语言')}</option>
            {LOCALES.map(l => <option key={l.code} value={l.code}>{l.code}</option>)}
          </select>
          <input style={{ ...S.input, width: 200 }} placeholder={t('固定译法（留空=不翻译）')} value={gForm.translation} onChange={e => setGForm({ ...gForm, translation: e.target.value })} />
          <button style={S.btnPrimary} onClick={addTerm}>{t('添加')}</button>
        </div>
        {glossary.map((g: any) => (
          <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
            <span><strong>{g.term}</strong>（{g.locale}）→ {g.translation || t('🔒 保持原文')}</span>
            <button style={S.btn} onClick={async () => { await del(`/api/v1/glossary/${g.id}`); load(); }}>{t('删除')}</button>
          </div>
        ))}
      </div>
    </>
  );
}

// ═══ 本地化任务 ═══
function LocalizeView() {
  const [rows, setRows] = useState<any[]>([]);
  const [markets, setMarkets] = useState<any[]>([]);
  const [locale, setLocale] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/localizations').then((r: any) => setRows(r.data || [])).catch(() => {});
    get('/api/v1/markets').then((r: any) => { setMarkets(r.data || []); if (r.data?.length && !locale) setLocale(r.data[0].locale); }).catch(() => {});
  }, [locale]);
  useEffect(load, [load]);

  const run = async () => {
    if (!locale) return alert('请先在"市场配置"添加目标市场');
    setBusy(true);
    try { const r: any = await post('/api/v1/localize/products', { locale, limit: 10 }); alert(`本地化 ${r.data.localized} 个商品，跳过未变更 ${r.data.skipped_unchanged} 个`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const check = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/consistency/check'); alert(`检查 ${r.data.checked} 条，发现 ${r.data.stale_found} 条源内容已变更（标记为 stale）`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/localizations/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  const apply = async (id: string) => {
    try { const r: any = await post(`/api/v1/localizations/${id}/apply`); alert(r.data?.hint || t('已回写 Shopify Translations')); setDetail(null); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <select style={S.input} value={locale} onChange={e => setLocale(e.target.value)}>
          {markets.map((m: any) => <option key={m.locale} value={m.locale}>{m.market_name}（{m.locale}）</option>)}
        </select>
        <button style={S.btnPrimary} disabled={busy} onClick={run}>{busy ? '本地化中（约 1-2 分钟）...' : t('🌍 本地化产品（10个/批）')}</button>
        <button style={S.btn} disabled={busy} onClick={check}>{t('🔄 一致性检查')}</button>
      </div>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>{detail.resource_title} → {detail.locale}</strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {detail.status !== 'applied' && <button style={S.btnPrimary} onClick={() => apply(detail.id)}>{t('✓ 回写 Shopify')}</button>}
              <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{t('源文本')}</div>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#fff', padding: 10, borderRadius: 6, maxHeight: 260, overflow: 'auto' }}>{detail.source_text}</pre>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#188038', marginBottom: 4 }}>本地化版（{detail.localized_title}）</div>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#fff', padding: 10, borderRadius: 6, maxHeight: 260, overflow: 'auto' }}>{detail.localized_text}</pre>
            </div>
          </div>
          {detail.quality_notes && <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>📋 适配说明：{detail.quality_notes}</div>}
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('资源')}</th><th style={S.th}>{t('类型')}</th><th style={S.th}>{t('语言')}</th><th style={S.th}>{t('适配说明')}</th><th style={S.th}>{t('状态')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                <td style={{ ...S.td, color: '#1a73e8' }}>{r.resource_title || r.resource_id}</td>
                <td style={S.td}>{r.resource_type}</td>
                <td style={S.td}>{r.locale}</td>
                <td style={{ ...S.td, fontSize: 12, maxWidth: 280 }}>{r.quality_notes}</td>
                <td style={{ ...S.td, color: r.status === 'stale' ? '#d93025' : r.status === 'applied' ? '#188038' : '#666' }}>
                  {r.status === 'stale' ? '⚠ 源已变更' : r.status}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无本地化记录')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 政策本地化 ═══
function PolicyView() {
  const [locale, setLocale] = useState('de');
  const [text, setText] = useState('');
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!text) return alert('请粘贴政策文本');
    setBusy(true);
    try { const r: any = await post('/api/v1/localize/policy', { locale, policy_text: text }); setResult(r.data); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('政策本地化（自动适配当地消费者保护法）')}</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <select style={S.input} value={locale} onChange={e => setLocale(e.target.value)}>
          {LOCALES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <button style={S.btnPrimary} disabled={busy} onClick={run}>{busy ? '处理中...' : t('🌍 本地化政策')}</button>
      </div>
      <textarea style={{ ...S.input, width: '100%', height: 140 }} placeholder={t('粘贴退换货/配送政策原文...')} value={text} onChange={e => setText(e.target.value)} />
      {result && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#d93025', marginBottom: 6 }}>⚖️ 合规调整：{result.compliance_notes}</div>
          <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: 12, borderRadius: 8, maxHeight: 300, overflow: 'auto' }}>{result.localized_text}</pre>
        </div>
      )}
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('localize');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>GlobalLocalizer</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('跨境本地化 Agent — 文化适配翻译 · 单位换算 · 法规适配 · 一致性管理')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'localize')} onClick={() => setTab('localize')}>{t('产品本地化')}</div>
        <div style={S.tab(tab === 'policy')} onClick={() => setTab('policy')}>{t('政策本地化')}</div>
        <div style={S.tab(tab === 'markets')} onClick={() => setTab('markets')}>{t('市场与术语库')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'localize' && <LocalizeView />}
      {tab === 'policy' && <PolicyView />}
      {tab === 'markets' && <MarketsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
